//---------------------------------------------------------
// Soft visual system — one set of tokens and MUI overrides shared by the
// Soft pages (Provision, Templates, Linode). Pages never restyle fields,
// menus, buttons or tables one by one; they render inside <SoftScope>,
// which layers these overrides on top of the app theme.
//---------------------------------------------------------
import {createTheme, Theme, ThemeOptions} from '@mui/material/styles';
import {FONT_MONO, FONT_SANS} from 'theme';

export const SOFT = {
	font: {sans: FONT_SANS, mono: FONT_MONO},
	color: {
		text: '#1B2733',
		textMuted: '#5A6B7D',
		placeholder: '#8A99A8',
		page: '#F6F8FA',
		surface: '#FFFFFF',
		border: '#C9D3DD',
		borderHover: '#8A99A8',
		borderFocus: '#113351',
		divider: '#E4E9EF',
		disabledFill: '#F0F3F6',
		disabledBorder: '#E4E9EF',
		disabledText: '#8A99A8',
		primary: '#113351',
		warning: '#B26A00',
		error: '#C62828',
		hover: 'rgba(17,51,81,0.04)',
		selected: 'rgba(17,51,81,0.08)',
		rowHover: 'rgba(17,51,81,0.03)',
	},
	type: {
		pageTitle: {fontSize: 20, fontWeight: 600, lineHeight: '28px'},
		sectionTitle: {fontSize: 15, fontWeight: 600, lineHeight: '22px'},
		label: {fontSize: 12, fontWeight: 600, lineHeight: '16px'},
		value: {fontSize: 13, fontWeight: 400, lineHeight: '20px'},
		helper: {fontSize: 12, fontWeight: 400, lineHeight: '16px'},
		tableHead: {fontSize: 12, fontWeight: 600, lineHeight: '16px'},
		button: {fontSize: 13, fontWeight: 600, lineHeight: '20px'},
	},
	size: {
		control: 36,
		controlPadX: 12,
		radius: 8,
		cardRadius: 12,
		cardPad: 20,
		labelGap: 6,
		helperGap: 4,
		fieldGap: 20,
		sectionGap: 32,
		pagePad: 24,
		titleGap: 20,
		selectArrow: 20,
		selectArrowRight: 8,
		clearIcon: 18,
		menuMaxHeight: 320,
		option: 32,
		tableHead: 40,
		tableRow: 44,
		cellPadX: 16,
		iconButton: 32,
		icon: 18,
		alertIcon: 18,
		scriptRows: 12,
		stackBelow: 900,
	},
} as const;

const C = SOFT.color;
const S = SOFT.size;

const valueType = {...SOFT.type.value, color: C.text};

const menuPaper = {
	borderRadius: S.radius,
	border: `1px solid ${C.divider}`,
	boxShadow: '0 4px 16px rgba(17,51,81,0.08)',
	marginTop: 4,
};

const optionRow = {
	...valueType,
	minHeight: S.option,
	padding: `0 ${S.controlPadX}px`,
};

/** MUI overrides for the Soft scope. Exported so tests can pin the spec. */
export const softThemeOptions: ThemeOptions = {
	typography: {fontFamily: FONT_SANS},
	components: {
		MuiOutlinedInput: {
			defaultProps: {notched: false},
			styleOverrides: {
				root: {
					...valueType,
					borderRadius: S.radius,
					backgroundColor: C.surface,
					'&:not(.MuiInputBase-multiline)': {height: S.control},
					'&:not(.MuiInputBase-multiline) .MuiOutlinedInput-input': {height: S.control, boxSizing: 'border-box'},
					'& .MuiOutlinedInput-notchedOutline': {borderColor: C.border, borderWidth: 1, top: 0},
					'& .MuiOutlinedInput-notchedOutline legend': {display: 'none'},
					'&:hover:not(.Mui-disabled):not(.Mui-focused):not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
						borderColor: C.borderHover,
					},
					'&.Mui-focused .MuiOutlinedInput-notchedOutline': {borderColor: C.borderFocus, borderWidth: 2},
					'&.Mui-error .MuiOutlinedInput-notchedOutline': {borderColor: C.error, borderWidth: 1},
					'&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': {borderColor: C.error, borderWidth: 2},
					'&.Mui-disabled': {backgroundColor: C.disabledFill, color: C.disabledText},
					'&.Mui-disabled .MuiOutlinedInput-notchedOutline': {borderColor: C.disabledBorder},
					'&.MuiInputBase-multiline': {padding: `10px ${S.controlPadX}px`},
				},
				input: {
					...valueType,
					height: 'auto',
					padding: `0 ${S.controlPadX}px`,
					'&::placeholder': {color: C.placeholder, opacity: 1, fontSize: 13},
					'&.Mui-disabled': {color: C.disabledText, WebkitTextFillColor: C.disabledText},
				},
				inputMultiline: {padding: 0},
			},
		},
		MuiSelect: {
			defaultProps: {
				MenuProps: {
					slotProps: {paper: {sx: {maxHeight: S.menuMaxHeight}}},
				},
			},
			styleOverrides: {
				select: {
					...valueType,
					display: 'flex',
					alignItems: 'center',
					minHeight: 'auto',
					height: S.control,
					boxSizing: 'border-box',
					paddingTop: 0,
					paddingBottom: 0,
					paddingLeft: S.controlPadX,
					'&.MuiSelect-select.MuiInputBase-input.MuiOutlinedInput-input': {
						paddingRight: S.selectArrowRight + S.selectArrow + 8,
					},
				},
				icon: {
					fontSize: S.selectArrow,
					width: S.selectArrow,
					height: S.selectArrow,
					right: S.selectArrowRight,
					top: `calc(50% - ${S.selectArrow / 2}px)`,
					color: C.textMuted,
				},
			},
		},
		MuiMenu: {
			styleOverrides: {
				paper: {...menuPaper, maxHeight: S.menuMaxHeight},
				list: {paddingTop: 4, paddingBottom: 4},
			},
		},
		MuiMenuItem: {
			styleOverrides: {
				root: {
					...optionRow,
					// beat MUI's sm-up `minHeight: auto` and 6px vertical padding
					'&.MuiMenuItem-root': {minHeight: S.option, paddingTop: 0, paddingBottom: 0},
					'&:hover, &.Mui-focusVisible': {backgroundColor: C.hover},
					'&.Mui-selected': {backgroundColor: C.selected},
					'&.Mui-selected:hover, &.Mui-selected.Mui-focusVisible': {backgroundColor: C.selected},
				},
			},
		},
		MuiAutocomplete: {
			styleOverrides: {
				root: {
					'& .MuiOutlinedInput-root': {padding: `0 ${S.controlPadX}px`},
					'& .MuiOutlinedInput-root .MuiAutocomplete-input': {padding: 0, height: S.control},
					'&.MuiAutocomplete-hasPopupIcon .MuiOutlinedInput-root, &.MuiAutocomplete-hasClearIcon .MuiOutlinedInput-root': {
						paddingRight: S.selectArrowRight + S.selectArrow + 8,
					},
					'&.MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon .MuiOutlinedInput-root': {
						paddingRight: S.selectArrowRight + S.selectArrow + S.clearIcon + 16,
					},
					'& .MuiOutlinedInput-root .MuiAutocomplete-endAdornment': {right: S.selectArrowRight - 2},
				},
				popupIndicator: {
					padding: 1,
					color: C.textMuted,
					'& .MuiSvgIcon-root': {fontSize: S.selectArrow},
				},
				clearIndicator: {
					padding: 2,
					color: C.textMuted,
					'& .MuiSvgIcon-root': {fontSize: S.clearIcon},
				},
				paper: menuPaper,
				listbox: {maxHeight: S.menuMaxHeight, paddingTop: 4, paddingBottom: 4},
				option: {
					...optionRow,
					'&.MuiAutocomplete-option': {minHeight: S.option, padding: `0 ${S.controlPadX}px`, alignItems: 'center'},
					'&.MuiAutocomplete-option.Mui-focused': {backgroundColor: C.hover},
					'&.MuiAutocomplete-option[aria-selected="true"], &.MuiAutocomplete-option[aria-selected="true"].Mui-focused': {
						backgroundColor: C.selected,
					},
				},
				groupLabel: {...SOFT.type.label, color: C.textMuted, lineHeight: `${S.option}px`, padding: `0 ${S.controlPadX}px`},
				noOptions: {...valueType, color: C.textMuted, padding: `6px ${S.controlPadX}px`},
			},
		},
		MuiButton: {
			defaultProps: {disableElevation: true},
			styleOverrides: {
				root: {
					...SOFT.type.button,
					height: S.control,
					padding: `0 16px`,
					borderRadius: S.radius,
					textTransform: 'none',
					whiteSpace: 'nowrap',
				},
				containedPrimary: {backgroundColor: C.primary},
				containedError: {backgroundColor: C.error},
				outlined: {
					padding: `0 16px`,
					borderColor: C.border,
					color: C.text,
					backgroundColor: C.surface,
					'&:hover': {borderColor: C.borderHover, backgroundColor: C.hover},
				},
				text: {padding: `0 16px`},
			},
		},
		MuiIconButton: {
			styleOverrides: {
				root: {
					width: S.iconButton,
					height: S.iconButton,
					padding: 0,
					borderRadius: S.radius,
					'& .MuiSvgIcon-root': {fontSize: S.icon},
				},
			},
		},
		MuiAlert: {
			styleOverrides: {
				root: {
					...SOFT.type.value,
					padding: '8px 12px',
					borderRadius: S.radius,
					alignItems: 'flex-start',
				},
				icon: {
					padding: '1px 0',
					marginRight: 8,
					opacity: 1,
					fontSize: S.alertIcon,
					'& .MuiSvgIcon-root': {fontSize: S.alertIcon},
				},
				message: {padding: 0, overflow: 'visible'},
				standardWarning: ({theme}) => ({border: `1px solid ${alpha30(theme.palette.warning.main)}`}),
				standardError: ({theme}) => ({border: `1px solid ${alpha30(theme.palette.error.main)}`}),
				standardSuccess: ({theme}) => ({border: `1px solid ${alpha30(theme.palette.success.main)}`}),
				standardInfo: ({theme}) => ({border: `1px solid ${alpha30(theme.palette.info.main)}`}),
			},
		},
		MuiPaper: {
			styleOverrides: {
				outlined: {borderColor: C.divider, borderRadius: S.cardRadius},
			},
		},
		MuiTableCell: {
			styleOverrides: {
				root: {
					...valueType,
					height: S.tableRow,
					padding: `0 ${S.cellPadX}px`,
					borderBottom: `1px solid ${C.divider}`,
				},
				head: {
					...SOFT.type.tableHead,
					height: S.tableHead,
					color: C.textMuted,
					backgroundColor: C.page,
					textTransform: 'none',
				},
			},
		},
		MuiTableRow: {
			styleOverrides: {
				root: {
					'&.MuiTableRow-hover:hover': {backgroundColor: C.rowHover},
					'&:last-of-type > .MuiTableCell-body': {borderBottom: 0},
				},
			},
		},
		MuiDialog: {
			styleOverrides: {
				paper: {borderRadius: S.cardRadius},
			},
		},
		MuiDialogTitle: {
			styleOverrides: {root: {...SOFT.type.sectionTitle, padding: `${S.cardPad}px ${S.cardPad}px 0`}},
		},
		MuiDialogContent: {
			styleOverrides: {
				root: {
					padding: `${S.cardPad}px`,
					'.MuiDialogTitle-root + &': {paddingTop: `${S.cardPad}px`},
				},
			},
		},
		MuiDialogActions: {
			styleOverrides: {root: {padding: `0 ${S.cardPad}px ${S.cardPad}px`, gap: 8}},
		},
	},
};

/** `#RRGGBB` → rgba at 30% (alert outline). */
export function alpha30(hex: string): string {
	const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
	if (!m) return hex;
	const [r, g, b] = m.slice(1).map(x => parseInt(x, 16));
	return `rgba(${r},${g},${b},0.3)`;
}

/** The app theme with the Soft overrides layered on top. */
export function buildSoftTheme(outer: Theme): Theme {
	return createTheme(outer, softThemeOptions);
}
